import type { DueLetterDeliveryStep } from "../../../../../../../../api/kehrblatt/letters"
import type { ScheduleEventCommunicationPresentation } from "../../../../../../../../api/kehrblatt/work-orders"
import { groupContactPointsByCustomerId } from "../../../../../../_shared/_logic/contact-quality-tags"
import { buildPropertyObjectTagPresentation } from "../../../../../../_shared/_logic/object-tags"
import {
  buildAppointmentSubLabel,
  resolveAppointmentContact,
} from "../../../../../../../../_shared/_logic/appointment-contact"
import { buildScheduleEventCommunicationTagPresentation } from "../../../../../../../../_shared/_logic/communication-presentation"
import {
  announcementContactOrderMatchesScheduleEvent,
  deriveCurrentAnnouncementContactOrderByWorkOrderId,
} from "../../../../../../../../_shared/_logic/announcement-presentation"
import {
  isActiveContactOrder,
  workOrderStatusLabels,
} from "../../../../../../../../_shared/_logic/outreach-presentation"
import {
  buildRichTooltip,
  tooltipField,
  tooltipList,
  tooltipText,
  tooltipTitle,
} from "../../../../../../../../_shared/_logic/rich-tooltips"
import {
  buildAppointmentStatusDefinition,
  buildStatusDescription,
  formatAddressTooltip,
  formatWorkOrderAppointmentLabel,
} from "../../context-tags"
import { buildWorkOrderSummaryTagPresentation } from "../../../../../../_shared/_logic/summary-tag"
import { buildAppointmentHistoryItems } from "../../../../../../_shared/_logic/appointment-history"
import { internalOrderTypeLabel } from "../../../../../../../../_shared/_logic/order-type-labels"
import {
  buildActionRequiredOverview,
  type WorkOrderActionRequiredProjection,
} from "../../../../../../_shared/_logic/action-required-overview"
import { buildAnnouncementOverview } from "../../../../../../_shared/_logic/announcement-overview"
import type {
  WorkOrderNotificationCountProjection,
  WorkOrderRow,
} from "../../../../../../_shared/_logic/work-orders-table-contract"
import { buildDueStatusPresentation, buildDueWarning } from "./dueStatus"
import { buildRecipientOverviewTag } from "./recipientOverview"
import {
  formatRoleLabel,
  sortRecipientsByContactOrder,
  workOrderUnitScope,
} from "./recipientPresentation"

export type { WorkOrderRow } from "../../../../../../_shared/_logic/work-orders-table-contract"

const resolveActionRequiredProjection = (
  order: WorkOrder,
): WorkOrderActionRequiredProjection | null => {
  const record = order as {
    action_required?: WorkOrderActionRequiredProjection | null
    actionRequired?: WorkOrderActionRequiredProjection | null
  }

  return record.action_required ?? record.actionRequired ?? null
}

const suppressStaleLetterDeliveryRequired = (
  actionRequired: WorkOrderActionRequiredProjection | null,
  dueLetterDeliverySteps: DueLetterDeliveryStep[],
): WorkOrderActionRequiredProjection | null => {
  if (actionRequired?.reason !== "letter_delivery_required") return actionRequired

  return dueLetterDeliverySteps.length ? actionRequired : null
}

type BillingPresentation = {
  pending: boolean
  totalCredits: number
  label: string
  tooltip: string
  color: WorkOrderRow["billedCreditsColor"]
}

type BillingComponentProjection = {
  kind?: string | null
  label?: string | null
  count?: number | null
  credits?: number | null
}

type BillingSummaryProjection = {
  total_credits?: number | null
  components?: BillingComponentProjection[] | null
  status?: string | null
}

type NormalizedBillingComponent = {
  kind: string
  label: string
  count: number
  credits: number
}

const billingComponentLabels: Record<string, string> = {
  work_order_completion: "Auftrag",
  mobile_message_delivery: "Mobile Nachricht",
  automatic_letter_delivery: "Briefversand",
}

const creditNumberFormatter = new Intl.NumberFormat("de-DE")

const normalizeCredits = (value: unknown): number => {
  const numeric = Number(value ?? 0)

  if (!Number.isFinite(numeric)) return 0

  return Math.max(0, Math.round(numeric))
}

const formatCredits = (credits: number): string =>
  `${creditNumberFormatter.format(credits)} Credit${credits === 1 ? "" : "s"}`

const formatBillingComponent = (component: NormalizedBillingComponent): string => {
  const positionLabel = `${component.count} Position${component.count === 1 ? "" : "en"}`

  return `${component.label}: ${positionLabel}, ${formatCredits(component.credits)}`
}

const normalizeBillingComponents = (
  components: BillingComponentProjection[] | null | undefined,
): NormalizedBillingComponent[] =>
  (Array.isArray(components) ? components : [])
    .map((component) => {
      const kind = `${component?.kind ?? ""}`.trim()
      const label = `${component?.label ?? billingComponentLabels[kind] ?? kind}`.trim()

      return {
        kind,
        label: label || "Kostenposition",
        count: normalizeCredits(component?.count),
        credits: normalizeCredits(component?.credits),
      }
    })
    .filter((component) => component.count > 0 || component.credits > 0)

const resolveBillingSummary = (
  order: WorkOrder,
): {
  totalCredits: number
  components: NormalizedBillingComponent[]
  status: string
} => {
  const record = order as {
    billed_credits?: number | null
    billing_summary?: BillingSummaryProjection | null
  }
  const summary = record.billing_summary ?? null

  if (!summary) {
    const totalCredits = normalizeCredits(record.billed_credits)

    return {
      totalCredits,
      components: totalCredits
        ? [
            {
              kind: "work_order_completion",
              label: "Auftrag",
              count: 1,
              credits: totalCredits,
            },
          ]
        : [],
      status: totalCredits > 0 ? "included" : "none",
    }
  }

  const components = normalizeBillingComponents(summary.components)
  const componentTotal = components.reduce((total, component) => total + component.credits, 0)
  const totalCredits =
    summary.total_credits === null || summary.total_credits === undefined
      ? componentTotal
      : normalizeCredits(summary.total_credits)
  const status = summary.status === "pending" ? "pending" : totalCredits > 0 ? "included" : "none"

  return { totalCredits, components, status }
}

const buildBillingPresentation = (order: WorkOrder): BillingPresentation => {
  const summary = resolveBillingSummary(order)
  const tooltip = buildRichTooltip([
    tooltipTitle("Kostenrechnung"),
    tooltipField("Gesamt", formatCredits(summary.totalCredits)),
    tooltipList("Aufschlüsselung", summary.components.map(formatBillingComponent)),
    summary.components.length || summary.status === "pending"
      ? null
      : tooltipText("Für diesen Auftrag wurden noch keine Credits abgerechnet."),
    summary.status === "pending"
      ? tooltipText("Einige Versandkosten sind vorgemerkt, aber noch nicht endgültig abgerechnet.")
      : null,
  ])

  return {
    totalCredits: summary.totalCredits,
    pending: summary.status === "pending",
    label: `${summary.totalCredits}`,
    tooltip,
    color: "neutral",
  }
}

export const buildWorkOrderRows = ({
  workOrders,
  propertyById,
  customersById,
  contactPoints = [],
  contactOrders = [],
  relevantScheduleEventByWorkOrderId = {},
  scheduleEvents = [],
  notificationCounts = [],
  dueLetterDeliveryStepsByWorkOrderId = {},
  communicationPresentationsByScheduleEventId = {},
  employees = [],
}: {
  workOrders: WorkOrder[]
  propertyById: Record<string, Property>
  customersById: Record<string, Customer>
  contactPoints?: ContactPoint[]
  orderTypes?: OrderType[]
  contactOrders?: ContactOrder[]
  relevantScheduleEventByWorkOrderId?: Record<string, ScheduleEvent>
  scheduleEvents?: ScheduleEvent[]
  notificationCounts?: WorkOrderNotificationCountProjection[]
  dueLetterDeliveryStepsByWorkOrderId?: Record<string, DueLetterDeliveryStep[]>
  communicationPresentationsByScheduleEventId?: Record<
    string,
    ScheduleEventCommunicationPresentation
  >
  employees?: unknown
}): WorkOrderRow[] => {
  const announcementContactOrderByWorkOrderId = deriveCurrentAnnouncementContactOrderByWorkOrderId(
    contactOrders,
    relevantScheduleEventByWorkOrderId,
  )
  const announcementStepsByWorkOrderId = Object.fromEntries(
    Object.entries(announcementContactOrderByWorkOrderId).map(([workOrderId, order]) => [
      workOrderId,
      Array.isArray(order.steps) ? order.steps : [],
    ]),
  )
  const contactPointsByCustomerId = groupContactPointsByCustomerId(contactPoints)
  const contactPointById = Object.fromEntries(
    contactPoints.filter((point) => point?.id).map((point) => [String(point.id), point]),
  )
  const employeeEntries = Array.isArray(employees) ? employees : []
  const employeeLabelById = Object.fromEntries(
    employeeEntries
      .filter((employee) => employee?.id)
      .map((employee) => [String(employee.id), employee.label || "Mitarbeiter:in"]),
  )
  const activeContactOrderIds = new Set(
    contactOrders
      .filter((order) => {
        if (!order?.work_order_id || !isActiveContactOrder(order)) return false

        return announcementContactOrderMatchesScheduleEvent(
          order,
          relevantScheduleEventByWorkOrderId[order.work_order_id],
        )
      })
      .map((order) => order.work_order_id as string),
  )
  const notificationCountByWorkOrderId = Object.fromEntries(
    notificationCounts
      .filter((entry) => entry?.work_order_id)
      .map((entry) => [entry.work_order_id as string, Number(entry.open_count ?? 0)]),
  )
  const notificationItemsByWorkOrderId = Object.fromEntries(
    notificationCounts
      .filter((entry) => entry?.work_order_id)
      .map((entry) => [entry.work_order_id as string, entry.open_items ?? []]),
  )

  return workOrders.map((order) => {
    const property = propertyById[order.property_id]
    const unitScope = workOrderUnitScope(property, order.property_unit_id)
    const sortedRecipients = sortRecipientsByContactOrder(order.recipients ?? [], unitScope)
    const relevantEvent = relevantScheduleEventByWorkOrderId[order.id]
    const announcementContactOrder = announcementContactOrderByWorkOrderId[order.id] ?? null
    const announcementSteps = announcementStepsByWorkOrderId[order.id] ?? []
    const dueLetterDeliverySteps = dueLetterDeliveryStepsByWorkOrderId[order.id] ?? []
    const notificationOpenCount = Math.max(0, Number(notificationCountByWorkOrderId[order.id] ?? 0))
    const notificationOpenItems = notificationItemsByWorkOrderId[order.id] ?? []
    const notificationTitles = notificationOpenItems
      .map((item) => `${item?.title ?? ""}`.trim())
      .filter(Boolean)
    const announcementOverview = buildAnnouncementOverview({
      contactOrder: announcementContactOrder,
      steps: announcementSteps,
      statusCode: ((order as any).view_status ?? null) as any,
      appointmentStatus: relevantEvent?.status ?? null,
      communicationPresentation: relevantEvent
        ? (communicationPresentationsByScheduleEventId[relevantEvent.id] ??
          relevantEvent.communication_presentation)
        : null,
      notificationOpenCount,
      notificationTitles,
    })
    const recipientTags = sortedRecipients.map((recipient, index) => {
      const customer = customersById[recipient.customer_id]
      const fallbackLabel = recipient.display_name ?? recipient.full_name ?? null

      return buildRecipientOverviewTag({
        id: recipient.customer_id || `${order.id}-recipient-${index}`,
        customer,
        fallbackLabel,
        contactPoints: contactPointsByCustomerId[recipient.customer_id] ?? [],
        roleLabel: formatRoleLabel(recipient.role),
        unitLabel: null,
        steps: announcementSteps,
        contactPointById,
      })
    })
    const recipientLabels = recipientTags.map((tag) => tag.label).filter(Boolean)
    const statusCode = (order as any).view_status ?? null
    const communicationPresentation = relevantEvent?.id
      ? (communicationPresentationsByScheduleEventId[relevantEvent.id] ??
        relevantEvent.communication_presentation ??
        null)
      : null
    const hasActiveContactOrder = order?.id ? activeContactOrderIds.has(order.id) : false
    const appointmentLabel = formatWorkOrderAppointmentLabel({
      event: relevantEvent,
      workOrder: order,
      statusCode,
    })
    const statusLabel = resolveWorkOrderStatusLabel({
      communicationPresentation,
      statusCode,
      appointmentStatus: relevantEvent?.status ?? null,
    })
    const actionRequiredOverview = buildActionRequiredOverview({
      actionRequired: suppressStaleLetterDeliveryRequired(
        resolveActionRequiredProjection(order),
        dueLetterDeliverySteps,
      ),
      appointmentLabel,
      announcementOverview,
    })
    const currentAppointmentEmployeeLabel = relevantEvent
      ? ((relevantEvent as any).employee_name ??
        (relevantEvent.employee_id
          ? (employeeLabelById[String(relevantEvent.employee_id)] ?? null)
          : null))
      : null
    const appointmentContact = resolveAppointmentContact({
      event: relevantEvent,
      workOrder: order,
      customersById,
      contactPoints,
    })
    const dueStatus = buildDueStatusPresentation({
      dueDate: order.due_date,
      statusCode,
      relevantEvent,
    })
    const orderSummary = buildWorkOrderSummaryTagPresentation({
      workOrder: order,
      color: "neutral",
      dueStatusDescription: dueStatus.definition?.description,
      appointmentLabel,
      employeeLabel: currentAppointmentEmployeeLabel,
      appointmentContact,
    })
    const orderTypeTitle = internalOrderTypeLabel(order)
    const dueLabel = orderSummary.dueLabel
    const billingPresentation = buildBillingPresentation(order)
    const propertyUnitFallback =
      (order as { property_unit_label?: string | null }).property_unit_label ?? null
    const propertyTag = buildPropertyObjectTagPresentation({
      property,
      propertyUnitId: order.property_unit_id,
      fallbackUnitLabel: propertyUnitFallback,
      mode: "address-first",
    })
    const communicationTagPresentation = relevantEvent
      ? buildScheduleEventCommunicationTagPresentation({
          status: relevantEvent.status,
          event: relevantEvent,
          presentation: communicationPresentation,
        })
      : null
    const appointmentContextSubLabel =
      appointmentLabel !== "Kein Termin"
        ? buildAppointmentSubLabel({
            employeeLabel: currentAppointmentEmployeeLabel,
            contactName: appointmentContact?.contactName,
            prefixEmployeeOnly: true,
          })
        : null
    const appointmentSubLabel =
      [communicationTagPresentation?.label, appointmentContextSubLabel]
        .filter((value): value is string => Boolean(value))
        .join(" · ") || null
    const appointmentStatusDefinition = buildAppointmentStatusDefinition({
      statusCode,
      appointmentLabel,
      appointmentStatus: relevantEvent?.status ?? null,
    })
    const canonicalAppointmentStatusDefinition =
      appointmentStatusDefinition && communicationTagPresentation
        ? {
            ...appointmentStatusDefinition,
            value: communicationTagPresentation.definition.value,
            color: communicationTagPresentation.definition.color,
            icon: communicationTagPresentation.definition.icon,
            description: communicationTagPresentation.detail,
          }
        : appointmentStatusDefinition
    const baseAppointmentTooltip = buildStatusDescription({
      order,
      statusCode,
      relevantEvent,
      hasActiveContactOrder,
      employeeLabel: currentAppointmentEmployeeLabel,
      appointmentContact,
      communicationDetail: communicationTagPresentation?.detail,
    })
    const appointmentTooltip = communicationTagPresentation?.tooltip || baseAppointmentTooltip
    const appointmentHistoryItems = buildAppointmentHistoryItems({
      workOrderId: order.id,
      events: scheduleEvents,
      currentEventId: relevantEvent?.id ?? null,
    })

    return {
      id: order.id,
      orderNumber: orderSummary.numberLabel,
      orderSummary: orderSummary.sortLabel,
      orderSummaryLabel: orderSummary.label,
      orderSummarySubLabel: orderSummary.subLabel,
      orderSummaryColor: orderSummary.color,
      dueWarning: order.archived_at ? null : buildDueWarning(dueStatus.definition),
      orderNumberLabel: orderSummary.numberLabel,
      orderType: orderSummary.label,
      orderTypeFullLabel: orderSummary.fullLabel,
      orderSummaryTooltip: orderSummary.tooltip,
      statusLabel,
      statusDescription: appointmentTooltip,
      statusRaw: statusCode,
      workOrder: order,
      propertyLabel: propertyTag.sortLabel,
      propertyTooltip: formatAddressTooltip(property),
      propertyAddressLines: [propertyTag.label, propertyTag.subLabel].filter(
        (line): line is string => Boolean(line),
      ),
      propertyTag,
      activeAppointmentLabel: appointmentLabel,
      appointmentSubLabel,
      currentAppointmentEmployeeLabel,
      currentAppointmentContactName: appointmentContact?.contactName ?? null,
      appointmentStatusDefinition: canonicalAppointmentStatusDefinition,
      appointmentTooltip: appointmentTooltip,
      appointmentHistoryItems,
      recipientLabels,
      recipientTags,
      recipientLabel: recipientLabels.length ? recipientLabels.join("\n") : "–",
      dueLabel,
      actionRequiredLabel: actionRequiredOverview.label,
      actionRequiredSubLabel: actionRequiredOverview.subLabel,
      actionRequiredStatusDefinition: actionRequiredOverview.definition,
      actionRequiredStatusTooltip: actionRequiredOverview.tooltip,
      actionRequiredOverview,
      notificationOpenCount,
      notificationCountLabel: notificationOpenCount ? `${notificationOpenCount} offen` : "–",
      notificationCountTooltip: notificationOpenCount
        ? buildRichTooltip([
            tooltipTitle(
              `${notificationOpenCount} offene Mitteilung${notificationOpenCount === 1 ? "" : "en"}`,
            ),
            tooltipList("Mitteilungen", notificationTitles),
          ])
        : "Keine offenen Mitteilungen zu diesem Auftrag.",
      billedCredits: billingPresentation.totalCredits,
      billedCreditsLabel: billingPresentation.label,
      billedCreditsColor: billingPresentation.color,
      billedCreditsPending: billingPresentation.pending,
      billedCreditsTooltip: billingPresentation.tooltip,
      billedCreditsTooltipHtml: true,
      dueStatusDefinition: dueStatus.definition,
      dueStatusTooltip: dueStatus.tooltip,
      workOrderSummaryTag: orderSummary,
      orderTypeTooltip: orderTypeTitle,
    }
  })
}

export const resolveWorkOrderStatusLabel = ({
  communicationPresentation,
  statusCode,
  appointmentStatus = null,
}: {
  communicationPresentation?: ScheduleEventCommunicationPresentation | null
  statusCode?: string | null
  appointmentStatus?: ScheduleEventStatus | null
}): string =>
  (communicationPresentation
    ? buildScheduleEventCommunicationTagPresentation({
        status: appointmentStatus ?? "RESERVED",
        presentation: communicationPresentation,
      }).label
    : null) ??
  workOrderStatusLabels[statusCode ?? ""] ??
  "Auftragsstatus unbekannt"
